import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { MetricChart } from "@/components/dashboard/metric-chart";
import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { PeriodToggle } from "@/components/dashboard/period-toggle";
import {
  getTikTokShopPerformance,
  type TikTokShopPerformance,
} from "@/services/tiktokShop.service";
import { formatAmount, formatNumber } from "@/utils/formatters";

/** Channel names TikTok reports GMV under, in the order the rows show them. */
const CHANNELS: Array<{ type: string; label: string }> = [
  { type: "VIDEO", label: "Video" },
  { type: "LIVE", label: "LIVE" },
  { type: "PRODUCT_CARD", label: "Product card" },
];

/**
 * Single-channel TikTok charts wear the brand color like every other chart;
 * the channel accent (--chart-tiktok) appears only where TikTok and Shopify
 * share one plot and two hues are unavoidable (the sales-by-channel card).
 */
const TIKTOK_COLOR = "var(--chart-1)";

function mean(values: number[]) {
  if (values.length === 0) return null;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

function totals(performance: TikTokShopPerformance | undefined) {
  const byChannel = new Map<string, number>();
  let gmv = 0;
  let orders = 0;
  const series: Record<string, number> = {};
  const visitors: number[] = [];
  const pageViews: number[] = [];
  const customers: number[] = [];
  const conversion: number[] = [];
  for (const interval of performance?.intervals ?? []) {
    const amount = Number(interval.gmv?.amount ?? 0) || 0;
    gmv += amount;
    orders += interval.ordersCount;
    series[interval.startDate] = amount;
    for (const part of interval.gmvBreakdowns) {
      byChannel.set(part.type, (byChannel.get(part.type) ?? 0) + (Number(part.amount) || 0));
    }
    if (interval.avgVisitors !== null) visitors.push(interval.avgVisitors);
    if (interval.avgPageViews !== null) pageViews.push(interval.avgPageViews);
    if (interval.avgCustomersCount !== null) customers.push(interval.avgCustomersCount);
    const rate = parseFloat(interval.avgConversionRate ?? "");
    if (!Number.isNaN(rate)) conversion.push(rate);
  }
  return {
    gmv,
    orders,
    series,
    byChannel,
    funnel: {
      visitors: mean(visitors),
      pageViews: mean(pageViews),
      customers: mean(customers),
      conversion: mean(conversion),
    },
  };
}

/**
 * TikTok Shop sales on the brand dashboard: period GMV and orders, the daily
 * GMV series, and how that GMV splits across video, LIVE and product-card
 * sales. Rendered only when a TikTok Shop connection is ACTIVE (the parent
 * decides); the figures come from TikTok's shop analytics, which TikTok
 * refreshes daily.
 */
export function TikTokShopCard() {
  const [days, setDays] = useState(30);
  const query = useQuery({
    queryKey: ["tiktok-shop-performance", days],
    queryFn: () => getTikTokShopPerformance(days),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const performance = query.data;
  const { gmv, orders, series, byChannel, funnel } = useMemo(() => totals(performance), [performance]);

  const split = CHANNELS.map((channel) => ({
    ...channel,
    amount: byChannel.get(channel.type) ?? 0,
  })).filter((channel) => channel.amount > 0);
  const splitMax = Math.max(...split.map((channel) => channel.amount), 0);

  return (
    <DashboardCard
      id="dashboard-tiktok-shop-card"
      title="TikTok Shop sales"
      action={<PeriodToggle value={days} onChange={setDays} disabled={query.isFetching} />}
      bodyClassName="pb-1"
    >
      {query.isError ? (
        <p className="px-5 pb-4 text-sm text-muted-foreground">
          TikTok Shop analytics could not be loaded. Try again in a few minutes.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 px-5 pb-3">
            <span className="text-2xl font-semibold tabular-nums text-foreground">
              {query.isLoading ? "—" : formatAmount(gmv)}
            </span>
            <span className="text-sm text-muted-foreground">
              {query.isLoading ? "Loading…" : `${formatNumber(orders)} paid orders on TikTok Shop`}
            </span>
          </div>
          <MetricChart
            color={TIKTOK_COLOR}
            metric={{
              label: "TikTok GMV",
              value: formatAmount(gmv),
              series,
              unit: "day",
              format: formatAmount,
            }}
          />
          {split.length > 0 && splitMax > 0 && (
            <div className="space-y-1.5 px-5 pb-4 pt-3">
              {split.map((channel) => (
                <div key={channel.type} className="flex items-center gap-2 text-xs">
                  <span className="w-20 shrink-0 text-muted-foreground">{channel.label}</span>
                  <span className="h-2 flex-1 rounded-full bg-muted">
                    <span
                      className="block h-2 rounded-full"
                      style={{
                        width: `${(channel.amount / splitMax) * 100}%`,
                        backgroundColor: TIKTOK_COLOR,
                      }}
                    />
                  </span>
                  <span className="w-20 shrink-0 text-right tabular-nums font-medium text-foreground">
                    {formatAmount(channel.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
          {funnel.visitors !== null && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-border px-5 py-3 text-xs text-muted-foreground">
              <span>
                <span className="tabular-nums font-medium text-foreground">{formatNumber(Math.round(funnel.visitors))}</span>{" "}
                visitors/day
              </span>
              {funnel.pageViews !== null && (
                <>
                  <span aria-hidden>→</span>
                  <span>
                    <span className="tabular-nums font-medium text-foreground">
                      {formatNumber(Math.round(funnel.pageViews))}
                    </span>{" "}
                    page views/day
                  </span>
                </>
              )}
              {funnel.customers !== null && (
                <>
                  <span aria-hidden>→</span>
                  <span>
                    <span className="tabular-nums font-medium text-foreground">
                      {formatNumber(Math.round(funnel.customers))}
                    </span>{" "}
                    buyers/day
                  </span>
                </>
              )}
              {funnel.conversion !== null && (
                <span className="ml-auto">
                  <span className="tabular-nums font-medium text-foreground">{funnel.conversion.toFixed(1)}%</span>{" "}
                  conversion
                </span>
              )}
            </div>
          )}
          {performance?.latestAvailableDate && (
            <p className="px-5 pb-3 text-xs text-muted-foreground">
              TikTok data through {performance.latestAvailableDate}.
            </p>
          )}
        </>
      )}
    </DashboardCard>
  );
}
